// Display preferences — small zustand store with localStorage persistence.
// Holds the fee-adjusted-odds toggle, optional maker rebate assumption,
// the user's default Value Dashboard
// landing pick (sport / market type / price mode), the order-ticket
// defaults (contracts qty + expiry mode), the order-cancel confirmation
// guard, per-surface view modes for positions / open-orders / trades, and
// bot-board view/sort defaults.
// Landing prefs are consumed at mount
// time so mid-session changes don't yank the active view; order ticket
// prefs are read live so changes apply on the next ticket render;
// view modes are read reactively so the on-page toggles flip
// the layout live.

import { create } from 'zustand';
import type { Sport, MarketType, PriceMode } from '../views/ValueDashboard/types';

const STORAGE_KEY = 'galactus:displaySettings';

/** Market types the dashboard tabs actually expose — narrows MarketType
 *  to the subset users can choose as a landing default. */
export type DashboardMarketType = 'moneyline' | 'spread-total' | 'series';

/** Default expiry mode seeded into every new order ticket. Mirrors the
 *  three buttons in the panel's Expiry row. */
export type DefaultExpiryMode = 'gts' | 'gtc' | 'custom';

/** Card vs table layout for open-orders / trades surfaces. */
export type SurfaceViewMode = 'card' | 'table';

/** Positions also get the dense compact-list view requested by traders. */
export type PositionViewMode = SurfaceViewMode | 'compact' | 'mini';

/** Edge bot boards share the compact/card/table renderer. */
export type BotBoardViewMode = 'compact' | 'card' | 'table';

/** Sort options exposed by the bot fleet boards. */
export type BotBoardSortKey = 'front' | 'eta' | 'net' | 'errors';

/** Sortable columns on the Value Dashboard's game table. Mirrors the
 *  SortableTh `sortKey` strings in GameTable.tsx — keep in sync if a new
 *  sortable column lands there. */
export type DashboardSortKey = 'date' | 'team' | 'queue' | 'velocity';
export type DashboardSortDir = 'asc' | 'desc';

/** Sort options for the Positions page's Open Orders surface. */
export type PositionsOrderSortKey = 'default' | 'size' | 'queue' | 'velocity';
export type PositionsOrderSortDir = 'asc' | 'desc';

/** Persisted default sort for the dashboard. `key: null` means "unsorted"
 *  — the table renders games in whatever order the upstream data source
 *  supplies, which is the prior factory behavior we preserve as the default
 *  so traders aren't subjected to a sort they didn't ask for. */
export interface DashboardSortDefault {
  key: DashboardSortKey | null;
  dir: DashboardSortDir;
}

export interface PositionsOrderSortDefault {
  key: PositionsOrderSortKey;
  dir: PositionsOrderSortDir;
}

interface PersistedShape {
  feeAdjustedOdds: boolean;
  /** When on, fold Kalshi's Market Maker Program rebate (50% pregame / 100%
   *  live reduction of the maker fee) into maker-perspective fee + odds
   *  displays. Only takes effect when the org is enrolled (AuthUser
   *  .mmRebateEnabled) and the Fees toggle is on. Replaces the retired
   *  notional-bps model. See [[project_kalshi_mm_rebate_model]]. */
  makerRebateEnabled: boolean;
  /** First sport the Value Dashboard lands on. */
  defaultSport: Sport;
  /** Which market-type tab opens first. */
  defaultMarketType: DashboardMarketType;
  /** Initial price mode (maker = best bid, taker = best ask, market = last). */
  defaultPriceMode: PriceMode;
  /** Price levels shown per side in the side-panel orderbooks (Exchange
   *  OrderBook maxLevels). Default 8 = the long-standing fan; Prof asked
   *  for a tighter "current ±3" view (Loom, Jun 12) — set 3. */
  bookDepthLevels: number;
  /** Initial expiry mode in the order ticket (GTS / GTC / Custom). */
  defaultExpiryMode: DefaultExpiryMode;
  /** Default trade size. NOT auto-populated on mount (that was a footgun
   *  — re-entering the panel could leave a phantom default in the Qty
   *  field). Instead used by the trade ticket as the value to fill the
   *  Qty input with when the user picks a price somewhere (orderbook
   *  level click, pill click, etc.) AND the Qty is currently blank.
   *  Also surfaced as the value of the "+ N" button next to the Qty
   *  label so the trader can apply it explicitly at any time. Integer
   *  ≥ 1. */
  defaultContracts: number;
  /** Layout for the Positions page's Open Positions surface. */
  positionViewMode: PositionViewMode;
  /** Layout for the Positions page's Open Orders surface. */
  orderViewMode: SurfaceViewMode;
  /** Layout for My Trades. */
  tradeViewMode: SurfaceViewMode;
  /** Initial sort for the Value Dashboard's game table. Consumed at mount
   *  time only — header clicks during the session do not flow back into
   *  this setting, matching the other landing prefs above. Factory default
   *  is `{ key: null }` (unsorted) so traders see the prior data-natural
   *  order until they explicitly pick a default here. */
  defaultDashboardSort: DashboardSortDefault;
  /** Initial Open Orders sort for the Positions page. Consumed at mount
   *  time so ad hoc header/chip clicks remain session-local. */
  defaultPositionsOrderSort: PositionsOrderSortDefault;
  /** Default layout for the standalone /bots pages. */
  botFleetViewMode: BotBoardViewMode;
  /** Default layout for the Position Tracker's claimed bot section. */
  myBotsViewMode: BotBoardViewMode;
  /** Default layout for the Position Tracker's org bot section. */
  orgBotsViewMode: BotBoardViewMode;
  /** Default sort for the standalone /bots pages. */
  botFleetSort: BotBoardSortKey;
  /** Default sort for the Position Tracker's claimed bot section. */
  myBotsSort: BotBoardSortKey;
  /** Default sort for the Position Tracker's org bot section. */
  orgBotsSort: BotBoardSortKey;
  /** When true, direct cancel buttons open a confirmation step before
   *  calling the cancel API. Defaults on so the new one-click icons stay
   *  guarded unless the trader explicitly opts out. */
  confirmOrderCancel: boolean;
  /** P&L dollar visibility on the positions page. Defaults OFF per
   *  stakeholder preference (Joshua, Jun 2: "sucks to see a negative") —
   *  traders opt in via the eye toggle next to the Unrealized P&L tile. */
  showPnl: boolean;
  /** Trade panel (side detail) scroll behavior. When true (DEFAULT), the order
   *  ticket stays pinned and only the order book scrolls within its own region —
   *  the classic desktop layout where BUY / Place Order are always in view. When
   *  false, the whole panel scrolls as one column so nothing is trapped on a
   *  short (laptop) viewport. */
  pinnedOrderTicket: boolean;
}

const DEFAULTS: PersistedShape = {
  feeAdjustedOdds: true,
  makerRebateEnabled: false,
  defaultSport: 'nba',
  defaultMarketType: 'moneyline',
  defaultPriceMode: 'maker',
  bookDepthLevels: 8,
  defaultExpiryMode: 'gts',
  defaultContracts: 1,
  positionViewMode: 'compact',
  orderViewMode: 'card',
  tradeViewMode: 'card',
  defaultDashboardSort: { key: null, dir: 'asc' },
  defaultPositionsOrderSort: { key: 'default', dir: 'asc' },
  botFleetViewMode: 'compact',
  myBotsViewMode: 'card',
  orgBotsViewMode: 'compact',
  botFleetSort: 'front',
  myBotsSort: 'front',
  orgBotsSort: 'front',
  confirmOrderCancel: true,
  showPnl: false,
  pinnedOrderTicket: true,
};

const VALID_SPORTS: ReadonlySet<Sport> = new Set<Sport>([
  'nba', 'wnba', 'nhl', 'cbb', 'cfb', 'atp', 'wta', 'mlb', 'ufc', 'esports', 'boxing', 'cricket', 'soccer',
]);
const VALID_MARKET_TYPES: ReadonlySet<DashboardMarketType> = new Set<DashboardMarketType>([
  'moneyline', 'spread-total', 'series',
]);
const VALID_PRICE_MODES: ReadonlySet<PriceMode> = new Set<PriceMode>([
  'maker', 'taker', 'market',
]);
const VALID_EXPIRY_MODES: ReadonlySet<DefaultExpiryMode> = new Set<DefaultExpiryMode>([
  'gts', 'gtc', 'custom',
]);
const VALID_SURFACE_VIEW_MODES: ReadonlySet<SurfaceViewMode> = new Set<SurfaceViewMode>([
  'card', 'table',
]);
const VALID_POSITION_VIEW_MODES: ReadonlySet<PositionViewMode> = new Set<PositionViewMode>([
  'compact', 'table', 'card', 'mini',
]);
const VALID_BOT_BOARD_VIEW_MODES: ReadonlySet<BotBoardViewMode> = new Set<BotBoardViewMode>([
  'compact', 'card', 'table',
]);
const VALID_DASHBOARD_SORT_KEYS: ReadonlySet<DashboardSortKey> = new Set<DashboardSortKey>([
  'date', 'team', 'queue', 'velocity',
]);
const VALID_POSITIONS_ORDER_SORT_KEYS: ReadonlySet<PositionsOrderSortKey> = new Set<PositionsOrderSortKey>([
  'default', 'size', 'queue', 'velocity',
]);
const VALID_BOT_BOARD_SORT_KEYS: ReadonlySet<BotBoardSortKey> = new Set<BotBoardSortKey>([
  'front', 'eta', 'net', 'errors',
]);
const VALID_SORT_DIRS: ReadonlySet<DashboardSortDir> = new Set<DashboardSortDir>([
  'asc', 'desc',
]);

/** Parse the persisted dashboard sort with the same defensive shape as the
 *  other validators — unknown key strings drop to factory default, missing
 *  fields fill in from factory. `key: null` is a valid persisted value
 *  (the "unsorted" sentinel). */
function readDashboardSort(raw: unknown): DashboardSortDefault {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS.defaultDashboardSort };
  const r = raw as { key?: unknown; dir?: unknown };
  const keyIsValid =
    r.key === null ||
    (typeof r.key === 'string' && VALID_DASHBOARD_SORT_KEYS.has(r.key as DashboardSortKey));
  const key = keyIsValid
    ? (r.key as DashboardSortKey | null)
    : DEFAULTS.defaultDashboardSort.key;
  const dir =
    typeof r.dir === 'string' && VALID_SORT_DIRS.has(r.dir as DashboardSortDir)
      ? (r.dir as DashboardSortDir)
      : DEFAULTS.defaultDashboardSort.dir;
  return { key, dir };
}

function readPositionsOrderSort(raw: unknown): PositionsOrderSortDefault {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS.defaultPositionsOrderSort };
  const r = raw as { key?: unknown; dir?: unknown };
  const validatedKey =
    typeof r.key === 'string' && VALID_POSITIONS_ORDER_SORT_KEYS.has(r.key as PositionsOrderSortKey)
      ? (r.key as PositionsOrderSortKey)
      : DEFAULTS.defaultPositionsOrderSort.key;
  // A saved DEFAULT must be a STABLE key. 'queue'/'velocity' re-order on every
  // market tick, so auto-loading into one made the positions view "spazz". Coerce
  // a persisted live default to the stable game-start order; traders can still pick
  // queue/velocity per-session (PositionTracker throttles those now).
  const key: PositionsOrderSortKey =
    validatedKey === 'queue' || validatedKey === 'velocity' ? 'default' : validatedKey;
  const dir =
    typeof r.dir === 'string' && VALID_SORT_DIRS.has(r.dir as PositionsOrderSortDir)
      ? (r.dir as PositionsOrderSortDir)
      : DEFAULTS.defaultPositionsOrderSort.dir;
  return { key, dir };
}

function readSurfaceViewMode(raw: unknown, fallback: SurfaceViewMode): SurfaceViewMode {
  return typeof raw === 'string' && VALID_SURFACE_VIEW_MODES.has(raw as SurfaceViewMode)
    ? (raw as SurfaceViewMode)
    : fallback;
}

function readPositionViewMode(raw: unknown, fallback: PositionViewMode): PositionViewMode {
  return typeof raw === 'string' && VALID_POSITION_VIEW_MODES.has(raw as PositionViewMode)
    ? (raw as PositionViewMode)
    : fallback;
}

function readBotBoardViewMode(raw: unknown, fallback: BotBoardViewMode): BotBoardViewMode {
  return typeof raw === 'string' && VALID_BOT_BOARD_VIEW_MODES.has(raw as BotBoardViewMode)
    ? (raw as BotBoardViewMode)
    : fallback;
}

function readBotBoardSortKey(raw: unknown, fallback: BotBoardSortKey): BotBoardSortKey {
  return typeof raw === 'string' && VALID_BOT_BOARD_SORT_KEYS.has(raw as BotBoardSortKey)
    ? (raw as BotBoardSortKey)
    : fallback;
}

function read(): PersistedShape {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<PersistedShape> & {
      /** Pre-split legacy preference. Intentionally not written anymore. */
      viewMode?: unknown;
    };
    const rawContracts = typeof parsed.defaultContracts === 'number'
      ? Math.floor(parsed.defaultContracts)
      : DEFAULTS.defaultContracts;
    return {
      feeAdjustedOdds: parsed.feeAdjustedOdds ?? DEFAULTS.feeAdjustedOdds,
      makerRebateEnabled:
        typeof parsed.makerRebateEnabled === 'boolean'
          ? parsed.makerRebateEnabled
          : DEFAULTS.makerRebateEnabled,
      defaultSport: parsed.defaultSport && VALID_SPORTS.has(parsed.defaultSport)
        ? parsed.defaultSport
        : DEFAULTS.defaultSport,
      defaultMarketType:
        parsed.defaultMarketType && VALID_MARKET_TYPES.has(parsed.defaultMarketType)
          ? parsed.defaultMarketType
          : DEFAULTS.defaultMarketType,
      defaultPriceMode:
        parsed.defaultPriceMode && VALID_PRICE_MODES.has(parsed.defaultPriceMode)
          ? parsed.defaultPriceMode
          : DEFAULTS.defaultPriceMode,
      bookDepthLevels:
        typeof parsed.bookDepthLevels === 'number' &&
        Number.isFinite(parsed.bookDepthLevels) &&
        parsed.bookDepthLevels >= 1 &&
        parsed.bookDepthLevels <= 20
          ? Math.floor(parsed.bookDepthLevels)
          : DEFAULTS.bookDepthLevels,
      defaultExpiryMode:
        parsed.defaultExpiryMode && VALID_EXPIRY_MODES.has(parsed.defaultExpiryMode)
          ? parsed.defaultExpiryMode
          : DEFAULTS.defaultExpiryMode,
      defaultContracts: Number.isFinite(rawContracts) && rawContracts >= 1
        ? rawContracts
        : DEFAULTS.defaultContracts,
      positionViewMode: readPositionViewMode(
        parsed.positionViewMode,
        DEFAULTS.positionViewMode,
      ),
      orderViewMode: readSurfaceViewMode(
        parsed.orderViewMode,
        DEFAULTS.orderViewMode,
      ),
      tradeViewMode: readSurfaceViewMode(
        parsed.tradeViewMode,
        DEFAULTS.tradeViewMode,
      ),
      defaultDashboardSort: readDashboardSort(parsed.defaultDashboardSort),
      defaultPositionsOrderSort: readPositionsOrderSort(parsed.defaultPositionsOrderSort),
      botFleetViewMode: readBotBoardViewMode(
        parsed.botFleetViewMode,
        DEFAULTS.botFleetViewMode,
      ),
      myBotsViewMode: readBotBoardViewMode(
        parsed.myBotsViewMode,
        DEFAULTS.myBotsViewMode,
      ),
      orgBotsViewMode: readBotBoardViewMode(
        parsed.orgBotsViewMode,
        DEFAULTS.orgBotsViewMode,
      ),
      botFleetSort: readBotBoardSortKey(parsed.botFleetSort, DEFAULTS.botFleetSort),
      myBotsSort: readBotBoardSortKey(parsed.myBotsSort, DEFAULTS.myBotsSort),
      orgBotsSort: readBotBoardSortKey(parsed.orgBotsSort, DEFAULTS.orgBotsSort),
      confirmOrderCancel:
        typeof parsed.confirmOrderCancel === 'boolean'
          ? parsed.confirmOrderCancel
          : DEFAULTS.confirmOrderCancel,
      showPnl:
        typeof parsed.showPnl === 'boolean' ? parsed.showPnl : DEFAULTS.showPnl,
      pinnedOrderTicket:
        typeof parsed.pinnedOrderTicket === 'boolean'
          ? parsed.pinnedOrderTicket
          : DEFAULTS.pinnedOrderTicket,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function write(s: PersistedShape) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // localStorage full or unavailable — silent
  }
}

interface DisplaySettingsStore extends PersistedShape {
  toggleFeeAdjustedOdds: () => void;
  toggleMakerRebate: () => void;
  setDefaultSport: (sport: Sport) => void;
  setDefaultMarketType: (mt: DashboardMarketType) => void;
  setDefaultPriceMode: (pm: PriceMode) => void;
  setBookDepthLevels: (levels: number) => void;
  setDefaultExpiryMode: (mode: DefaultExpiryMode) => void;
  setDefaultContracts: (n: number) => void;
  setPositionViewMode: (m: PositionViewMode) => void;
  setOrderViewMode: (m: SurfaceViewMode) => void;
  setTradeViewMode: (m: SurfaceViewMode) => void;
  setDefaultDashboardSort: (sort: DashboardSortDefault) => void;
  setDefaultPositionsOrderSort: (sort: PositionsOrderSortDefault) => void;
  setBotFleetViewMode: (m: BotBoardViewMode) => void;
  setMyBotsViewMode: (m: BotBoardViewMode) => void;
  setOrgBotsViewMode: (m: BotBoardViewMode) => void;
  setBotFleetSort: (key: BotBoardSortKey) => void;
  setMyBotsSort: (key: BotBoardSortKey) => void;
  setOrgBotsSort: (key: BotBoardSortKey) => void;
  setConfirmOrderCancel: (enabled: boolean) => void;
  toggleShowPnl: () => void;
  setPinnedOrderTicket: (enabled: boolean) => void;
}

function snapshot(s: PersistedShape): PersistedShape {
  return {
    feeAdjustedOdds: s.feeAdjustedOdds,
    makerRebateEnabled: s.makerRebateEnabled,
    defaultSport: s.defaultSport,
    defaultMarketType: s.defaultMarketType,
    defaultPriceMode: s.defaultPriceMode,
    bookDepthLevels: s.bookDepthLevels,
    defaultExpiryMode: s.defaultExpiryMode,
    defaultContracts: s.defaultContracts,
    positionViewMode: s.positionViewMode,
    orderViewMode: s.orderViewMode,
    tradeViewMode: s.tradeViewMode,
    defaultDashboardSort: s.defaultDashboardSort,
    defaultPositionsOrderSort: s.defaultPositionsOrderSort,
    botFleetViewMode: s.botFleetViewMode,
    myBotsViewMode: s.myBotsViewMode,
    orgBotsViewMode: s.orgBotsViewMode,
    botFleetSort: s.botFleetSort,
    myBotsSort: s.myBotsSort,
    orgBotsSort: s.orgBotsSort,
    confirmOrderCancel: s.confirmOrderCancel,
    showPnl: s.showPnl,
    pinnedOrderTicket: s.pinnedOrderTicket,
  };
}

export const useDisplaySettings = create<DisplaySettingsStore>((set, get) => ({
  ...read(),
  toggleFeeAdjustedOdds: () => {
    set((s) => ({ feeAdjustedOdds: !s.feeAdjustedOdds }));
    write(snapshot(get()));
  },
  toggleMakerRebate: () => {
    set((s) => ({ makerRebateEnabled: !s.makerRebateEnabled }));
    write(snapshot(get()));
  },
  toggleShowPnl: () => {
    set((s) => ({ showPnl: !s.showPnl }));
    write(snapshot(get()));
  },
  setDefaultSport: (sport) => {
    set({ defaultSport: sport });
    write(snapshot(get()));
  },
  setDefaultMarketType: (mt) => {
    set({ defaultMarketType: mt });
    write(snapshot(get()));
  },
  setDefaultPriceMode: (pm) => {
    set({ defaultPriceMode: pm });
    write(snapshot(get()));
  },
  setBookDepthLevels: (levels) => {
    const clean =
      Number.isFinite(levels) && levels >= 1 && levels <= 20 ? Math.floor(levels) : DEFAULTS.bookDepthLevels;
    set({ bookDepthLevels: clean });
    write(snapshot(get()));
  },
  setDefaultExpiryMode: (mode) => {
    set({ defaultExpiryMode: mode });
    write(snapshot(get()));
  },
  setDefaultContracts: (n) => {
    const clean = Number.isFinite(n) && n >= 1 ? Math.floor(n) : DEFAULTS.defaultContracts;
    set({ defaultContracts: clean });
    write(snapshot(get()));
  },
  setPositionViewMode: (m) => {
    set({
      positionViewMode: VALID_POSITION_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.positionViewMode,
    });
    write(snapshot(get()));
  },
  setOrderViewMode: (m) => {
    set({
      orderViewMode: VALID_SURFACE_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.orderViewMode,
    });
    write(snapshot(get()));
  },
  setTradeViewMode: (m) => {
    set({
      tradeViewMode: VALID_SURFACE_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.tradeViewMode,
    });
    write(snapshot(get()));
  },
  setDefaultDashboardSort: (sort) => {
    // Defensive normalization — if a caller hands us a key not in the
    // current sortable-columns list, fall back to factory default rather
    // than persisting nonsense that confuses the next read().
    const key =
      sort.key === null || VALID_DASHBOARD_SORT_KEYS.has(sort.key)
        ? sort.key
        : DEFAULTS.defaultDashboardSort.key;
    const dir = VALID_SORT_DIRS.has(sort.dir)
      ? sort.dir
      : DEFAULTS.defaultDashboardSort.dir;
    set({ defaultDashboardSort: { key, dir } });
    write(snapshot(get()));
  },
  setDefaultPositionsOrderSort: (sort) => {
    const key = VALID_POSITIONS_ORDER_SORT_KEYS.has(sort.key)
      ? sort.key
      : DEFAULTS.defaultPositionsOrderSort.key;
    const dir = VALID_SORT_DIRS.has(sort.dir)
      ? sort.dir
      : DEFAULTS.defaultPositionsOrderSort.dir;
    set({ defaultPositionsOrderSort: { key, dir } });
    write(snapshot(get()));
  },
  setBotFleetViewMode: (m) => {
    set({
      botFleetViewMode: VALID_BOT_BOARD_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.botFleetViewMode,
    });
    write(snapshot(get()));
  },
  setMyBotsViewMode: (m) => {
    set({
      myBotsViewMode: VALID_BOT_BOARD_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.myBotsViewMode,
    });
    write(snapshot(get()));
  },
  setOrgBotsViewMode: (m) => {
    set({
      orgBotsViewMode: VALID_BOT_BOARD_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.orgBotsViewMode,
    });
    write(snapshot(get()));
  },
  setBotFleetSort: (key) => {
    set({
      botFleetSort: VALID_BOT_BOARD_SORT_KEYS.has(key)
        ? key
        : DEFAULTS.botFleetSort,
    });
    write(snapshot(get()));
  },
  setMyBotsSort: (key) => {
    set({
      myBotsSort: VALID_BOT_BOARD_SORT_KEYS.has(key)
        ? key
        : DEFAULTS.myBotsSort,
    });
    write(snapshot(get()));
  },
  setOrgBotsSort: (key) => {
    set({
      orgBotsSort: VALID_BOT_BOARD_SORT_KEYS.has(key)
        ? key
        : DEFAULTS.orgBotsSort,
    });
    write(snapshot(get()));
  },
  setConfirmOrderCancel: (enabled) => {
    set({ confirmOrderCancel: enabled });
    write(snapshot(get()));
  },
  setPinnedOrderTicket: (enabled) => {
    set({ pinnedOrderTicket: enabled });
    write(snapshot(get()));
  },
}));

// Cross-tab sync. Each tab keeps its own in-memory copy; without this, a tab
// that changed one field later writes its WHOLE (now-stale) snapshot and clobbers
// a sibling tab's change — the "settings reset on refresh" traders report with
// two tabs/windows open. The `storage` event fires only in OTHER tabs (never the
// writer), so re-hydrating from localStorage here can't loop, and setState merges
// over the existing setters. A real localStorage.clear() fires with key===null
// (ignored by the guard below), so only a direct write to STORAGE_KEY triggers a
// re-read — and removeItem of this key never happens in app code.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      useDisplaySettings.setState(read());
    }
  });
}
